import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClaims, updateClaimStatus } from "../../store/thunks/claimsThunk";
import { clearCurrentClaim } from "../../store/slices/claimsSlice";
import Table from "../../applicationUi/components/Table";
import { toast } from "react-toastify";

const statusColors = {
  draft: "bg-gray-100 text-gray-800",
  ready: "bg-blue-100 text-blue-800",
  submitted: "bg-yellow-100 text-yellow-800",
  accepted: "bg-indigo-100 text-indigo-800",
  rejected: "bg-red-100 text-red-800",
  in_adjudication: "bg-purple-100 text-purple-800",
  paid: "bg-green-100 text-green-800",
  partial_paid: "bg-emerald-100 text-emerald-800",
  denied: "bg-red-200 text-red-900",
  appealed: "bg-orange-100 text-orange-800",
  void: "bg-gray-200 text-gray-600",
};

const statusLabels = {
  draft: "Draft",
  ready: "Ready",
  submitted: "Submitted",
  accepted: "Accepted",
  rejected: "Rejected",
  in_adjudication: "In Adjudication",
  paid: "Paid",
  partial_paid: "Partial Paid",
  denied: "Denied",
  appealed: "Appealed",
  void: "Void",
};

export default function ClaimsPage() {
  const dispatch = useDispatch();
  const { claims, loading, pagination } = useSelector((state) => state.claims);
  const currentUser = useSelector((state) => state.auth.user);
  const selectedClinic = useSelector((state) => state.clinics.selectedClinic);

  const [statusFilter, setStatusFilter] = useState("");
  const [selectedClaim, setSelectedClaim] = useState(null);

  useEffect(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (selectedClinic?._id) params.clinic = selectedClinic._id;
    dispatch(fetchClaims(params));
  }, [dispatch, statusFilter, selectedClinic]);

  const handleStatusChange = async (claimId, newStatus) => {
    try {
      await dispatch(
        updateClaimStatus({
          id: claimId,
          status: newStatus,
          userId: currentUser._id,
          notes: `Status changed to ${newStatus}`,
        })
      ).unwrap();
      toast.success(`Claim status updated to ${statusLabels[newStatus]}`);
      setSelectedClaim(null);
    } catch (error) {
      toast.error("Failed to update claim status");
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "—";
    return `$${Number(amount).toFixed(2)}`;
  };

  const columns = [
    { header: "Claim #", accessor: "claimNumber" },
    {
      header: "Patient",
      accessor: "patient",
      cell: (row) => `${row.patient?.lastName}, ${row.patient?.firstName}`,
    },
    {
      header: "DOS",
      accessor: "serviceLines",
      cell: (row) => formatDate(row.serviceLines?.[0]?.dateOfServiceFrom),
    },
    {
      header: "Status",
      accessor: "status",
      cell: (row) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[row.status] || "bg-gray-100 text-gray-800"}`}>
          {statusLabels[row.status] || row.status}
        </span>
      ),
    },
    {
      header: "Total",
      accessor: "totalCharge",
      cell: (row) => formatCurrency(row.totalCharge),
    },
    {
      header: "Paid",
      accessor: "paidAmount",
      cell: (row) => formatCurrency(row.paidAmount),
    },
    {
      header: "Created",
      accessor: "createdAt",
      cell: (row) => formatDate(row.createdAt),
    },
  ];

  const handleRowClick = (claimId) => {
    const claim = claims.find((c) => c._id === claimId);
    setSelectedClaim(claim);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Status filter bar */}
      <div className="mb-4 flex flex-wrap gap-2">
        <button
          onClick={() => setStatusFilter("")}
          className={`px-3 py-1 text-sm rounded-full ${!statusFilter ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
        >
          All
        </button>
        {Object.entries(statusLabels).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            className={`px-3 py-1 text-sm rounded-full ${statusFilter === key ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <Table
        columns={columns}
        data={claims}
        onRowClick={(row) => handleRowClick(row)}
        title="Insurance Claims"
        description="Manage insurance claims and track submission status."
      />

      {/* Claim Detail Panel */}
      {selectedClaim && (
        <div className="fixed inset-0 z-10 overflow-y-auto" onClick={() => setSelectedClaim(null)}>
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="relative bg-white rounded-sm shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Claim {selectedClaim.claimNumber}
                  </h2>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium mt-1 ${statusColors[selectedClaim.status]}`}>
                    {statusLabels[selectedClaim.status]}
                  </span>
                </div>
                <button onClick={() => setSelectedClaim(null)} className="text-gray-400 hover:text-gray-500">
                  &times;
                </button>
              </div>

              {/* Patient Info */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Patient</h3>
                  <p className="text-sm text-gray-900">
                    {selectedClaim.patient?.firstName} {selectedClaim.patient?.lastName}
                  </p>
                  {selectedClaim.patient?.dateOfBirth && (
                    <p className="text-sm text-gray-600">DOB: {formatDate(selectedClaim.patient.dateOfBirth)}</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Insurance</h3>
                  <p className="text-sm text-gray-900">Member ID: {selectedClaim.insurance?.memberID || "—"}</p>
                  <p className="text-sm text-gray-600">Group: {selectedClaim.insurance?.groupNumber || "—"}</p>
                </div>
              </div>

              {/* Diagnosis Codes */}
              {selectedClaim.diagnosisCodes?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Diagnosis Codes</h3>
                  <div className="space-y-1">
                    {selectedClaim.diagnosisCodes.map((dx, i) => (
                      <p key={i} className="text-sm text-gray-900">
                        <span className="font-mono font-medium">{dx.pointer}.</span> {dx.code} — {dx.description}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Service Lines */}
              {selectedClaim.serviceLines?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Service Lines</h3>
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">CPT</th>
                        <th className="text-left py-1">Modifiers</th>
                        <th className="text-left py-1">POS</th>
                        <th className="text-right py-1">Units</th>
                        <th className="text-right py-1">Charges</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedClaim.serviceLines.map((line, i) => (
                        <tr key={i} className="border-b">
                          <td className="py-1 font-mono">{line.cptCode}</td>
                          <td className="py-1">{line.modifiers?.join(", ") || "—"}</td>
                          <td className="py-1">{line.placeOfService}</td>
                          <td className="py-1 text-right">{line.units}</td>
                          <td className="py-1 text-right">{formatCurrency(line.charges)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4" className="py-1 text-right font-medium">Total:</td>
                        <td className="py-1 text-right font-medium">{formatCurrency(selectedClaim.totalCharge)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Financial Summary */}
              <div className="grid grid-cols-3 gap-4 mb-4 bg-gray-50 p-3 rounded-sm">
                <div>
                  <p className="text-xs text-gray-500">Total Charges</p>
                  <p className="text-sm font-semibold">{formatCurrency(selectedClaim.totalCharge)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Amount Paid</p>
                  <p className="text-sm font-semibold text-green-600">{formatCurrency(selectedClaim.paidAmount)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Patient Responsibility</p>
                  <p className="text-sm font-semibold">{formatCurrency(selectedClaim.patientResponsibilityAmount)}</p>
                </div>
              </div>

              {/* Status Actions */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Update Status</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedClaim.status === "draft" && (
                    <button onClick={() => handleStatusChange(selectedClaim._id, "ready")}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-sm hover:bg-blue-500">
                      Mark Ready
                    </button>
                  )}
                  {selectedClaim.status === "ready" && (
                    <button onClick={() => handleStatusChange(selectedClaim._id, "submitted")}
                      className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded-sm hover:bg-yellow-500">
                      Mark Submitted
                    </button>
                  )}
                  {selectedClaim.status === "submitted" && (
                    <>
                      <button onClick={() => handleStatusChange(selectedClaim._id, "accepted")}
                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-sm hover:bg-indigo-500">
                        Accepted
                      </button>
                      <button onClick={() => handleStatusChange(selectedClaim._id, "rejected")}
                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-sm hover:bg-red-500">
                        Rejected
                      </button>
                    </>
                  )}
                  {["accepted", "in_adjudication"].includes(selectedClaim.status) && (
                    <>
                      <button onClick={() => handleStatusChange(selectedClaim._id, "paid")}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-sm hover:bg-green-500">
                        Paid
                      </button>
                      <button onClick={() => handleStatusChange(selectedClaim._id, "denied")}
                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-sm hover:bg-red-500">
                        Denied
                      </button>
                      <button onClick={() => handleStatusChange(selectedClaim._id, "partial_paid")}
                        className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-sm hover:bg-emerald-500">
                        Partial Pay
                      </button>
                    </>
                  )}
                  {selectedClaim.status === "denied" && (
                    <button onClick={() => handleStatusChange(selectedClaim._id, "appealed")}
                      className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded-sm hover:bg-orange-500">
                      Appeal
                    </button>
                  )}
                  {!["void", "paid"].includes(selectedClaim.status) && (
                    <button onClick={() => handleStatusChange(selectedClaim._id, "void")}
                      className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded-sm hover:bg-gray-500">
                      Void
                    </button>
                  )}
                </div>
              </div>

              {/* Status History */}
              {selectedClaim.statusHistory?.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Status History</h3>
                  <div className="space-y-1">
                    {selectedClaim.statusHistory.map((entry, i) => (
                      <p key={i} className="text-xs text-gray-600">
                        {formatDate(entry.timestamp)} — {statusLabels[entry.status] || entry.status}
                        {entry.notes && ` (${entry.notes})`}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
